import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageSEO, PageFAQSchema } from '../../components/seo/SchemaMarkup';
import {
  API, EmailCapture, Eyebrow, FacilityCard, FaqBlock, LocatorLoading, StatBar,
  localBusinessList, useJsonLd, useLocatorPageView,
} from './locatorShared';

export default function LocatorTherapyMetro() {
  const { metro, therapy } = useParams();
  useLocatorPageView(`${metro}/${therapy}`);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setNotFound(false);
    fetch(`${API}/api/locator/metro/${metro}/${therapy}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setNotFound(true));
  }, [metro, therapy]);

  useJsonLd(
    'locator-tm-itemlist',
    data ? localBusinessList(data.facilities, `/locator/${metro}/${therapy}`, `Best ${data.therapy.name} in ${data.metro.name}`) : null,
  );

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Page Not Found</h1>
          <Link to="/locator" className="text-[#C7FA3E] text-sm underline underline-offset-2">Browse the recovery locator</Link>
        </div>
      </div>
    );
  }
  if (!data) return <LocatorLoading />;

  const t = data.therapy;
  const m = data.metro;
  return (
    <article className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid={`locator-tm-${metro}-${therapy}`}>
      <PageSEO
        title={`Best ${t.name} in ${m.name}${m.state ? ` (${m.state})` : ''} — ${t.count} Vetted Clinics 2026 | NinjAthlete`}
        description={`Find ${t.name.toLowerCase()} in ${m.name}. ${t.count} vetted facilities with contact details, websites and booking links.`}
        path={`/locator/${metro}/${therapy}`}
        breadcrumbs={[
          { name: 'Home', url: '/' }, { name: 'Recovery Locator', url: '/locator' },
          { name: m.name, url: `/locator/${metro}` }, { name: t.name, url: `/locator/${metro}/${therapy}` },
        ]}
      />
      <PageFAQSchema faqs={data.faqs} pageName={`Best ${t.name} in ${m.name}`} pagePath={`/locator/${metro}/${therapy}`} scriptId="locator-tm-faq" />
      <div className="max-w-5xl mx-auto">
        <Eyebrow>
          <Link to={`/locator/${metro}`} className="hover:underline">{m.name}</Link> · {t.name} · Updated August 2026
        </Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="locator-tm-h1">
          The Best {t.name} in {m.name}
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-2xl">
          {t.blurb} <strong className="text-white">{t.count} vetted {m.name} facilities</strong> offer it.
          {t.pillar && <> Learn the protocol first in <a href={t.pillar} className="text-[#C7FA3E] hover:underline">our guide</a>.</>}
        </p>

        <StatBar
          testPrefix={`tm-${metro}-${therapy}`}
          stats={[
            [`${t.count}`, `${m.name} facilities`],
            [`${data.sibling_pages.length + 1}`, 'Therapy guides here'],
            ['Vetted', 'Every listing'],
            ['Free', 'To claim a listing'],
          ]}
        />

        <EmailCapture metro={metro} metroName={m.name} testPrefix={`tm-${metro}-${therapy}`} />

        <div className="space-y-4 mt-6">
          {data.facilities.map((f) => <FacilityCard key={f.slug} facility={f} page={`${metro}-${therapy}`} />)}
        </div>

        <FaqBlock faqs={data.faqs} testPrefix={`tm-${metro}-${therapy}`} />

        <section className="mt-12">
          <h2 className="text-white text-2xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>More in {m.name}</h2>
          <div className="flex flex-wrap gap-2">
            <Link to={`/locator/${metro}`} className="px-3 py-1.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity">
              All {m.name} facilities
            </Link>
            {data.sibling_pages.map((p) => (
              <Link key={p.url} to={p.url} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors">
                {p.name} ({p.count})
              </Link>
            ))}
            <Link to={data.national_hub} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors">
              {t.name} nationwide
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
